<template>
    <video v-if="hasErrored && type === 1 && allowVideoFallback" :src="videoURL" />
    <div v-else-if="hasErrored" style="height: 100%" class="d-flex justify-center align-center flex-column">
        <font-awesome-icon style="font-size: 1.5rem" icon="fa-regular fa-image"></font-awesome-icon>
        <p class="text-caption mt-2">Format does not support preview</p>
    </div>
    <img v-else :src="thumbnailURL" @error="thumbnailLoadError" @load="onLoadFinish" :key="`${thumbnailURL}_${retries}`"/>
</template>

<script>
import { tributeMediaTypeEnum } from '../../constants';
const MAX_RETRIES = 6;
const RETRY_TIMEOUT = 1000;
const RETRY_PAD = .75;

export default {
    name: 'ImageThumbnail',
    props: {
        src: {
            type: String,
            required: true,
        },

        h: {
            type: [Number, null],
            default: 60,
        },
        w: {
            type: [Number, null],
            default: 60,
        },
        smartCrop: {
            type: Boolean,
            default: false,
        },
        type: {
            type: Number,
            // Image: 0, Video: 1, Title: 2
            default: 0,
        },
        allowVideoFallback: {
            type: Boolean,
            default: true,
        },
        preset: {
            type: String,
            default: null,
            validator(value) {
                return ['small', 'medium', 'large', 'original'].includes(value);
            },
        },
        retryOnFail: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            tributeMediaTypeEnum,
            hasErrored: false,
            retries: 0,
        };
    },
    computed: {
        // Presets matched to prefetched imageKit transforms for optimized delivery (tribute photos only)
        //Preset takes precedence, then prop
        presetConfigs() {
            return {
                small: { w: 80, h: 80, smartCrop: true },
                medium: { w: 400, h: null, smartCrop: true },
                large: { w: 800, h: null, smartCrop: true },
                original: { w: null, h: null, smartCrop: false },
            };
        },
        computedW() {
            if (this.preset && this.presetConfigs[this.preset]) {
                return this.presetConfigs[this.preset].w;
            }

            return this.w;
        },
        computedH() {
            if (this.preset && this.presetConfigs[this.preset]) {
                return this.presetConfigs[this.preset].h;
            }

            return this.h;
        },
        computedSmartCrop() {
            if (this.preset && this.presetConfigs[this.preset]) {
                return this.presetConfigs[this.preset].smartCrop;
            }
            return this.smartCrop;
        },
        transforms() {
            let transforms = [];
            if (this.computedW) {
                transforms.push(`w-${this.computedW}`);
            }
            if (this.computedH) {
                transforms.push(`h-${this.computedH}`);
            }
            if (this.computedSmartCrop && !this.isVideoSlide) {
                //this transformation breaks img kit video thumbnails
                transforms.push('fo-auto');
            }
            if (this.imageType === 'tribute-template-renders' || this.isVideoSlide) {
                transforms.push('so-2');
            }
            if (transforms.length) {
                if (this.isVideoSlide) {
                    return `tr=${transforms.join()}`;
                }
    
                return `tr:${transforms.join()}`;
            }
            return '';
        },
        imagePath() {
            switch (this.imageType) {
                case 'genre-images':
                    return this.imageType;
                case 'artwork-image':
                    return 'album-art';
                case 'original-tribute-photos':
                case 'tribute-photos':
                    return '';
                case 'tribute-template-renders':
                    return 'templates';
                default:
                    return '';
            }
        },
        imageType() {
            // Genre
            if (this.$props.src.includes('genre-image')) {
                return 'genre-images';
            }
            // Music Artwork
            if (this.$props.src.includes('artwork-image')) {
                return 'artwork-image';
            }
            // OG Tribute Photo
            if (this.$props.src.includes('original-tribute-photos')) {
                return 'original-tribute-photos';
            }
            // Tribute Photo
            if (this.$props.src.includes('tribute-photos')) {
                return 'tribute-photos';
            }
            // Tribute Photo
            if (this.$props.src.includes('tribute-template-renders')) {
                return 'tribute-template-renders';
            }
        },
        suffix() {
            if (this.isVideoSlide) {
                return '/ik-thumbnail.jpg';
            }
            return '';
        },
        thumbnailURL() {
            // Handle media that is already is a imagekit formatted urls
            if (this.$props.src.startsWith(process.env.VUE_APP_IMG_KIT_BASE)) {
                const tmpTransforms = this.transforms.replace('tr:', '').replace('tr=', '');

                if (this.isVideoSlide) {
                    if (this.$props.src.includes('?tr=')) {
                        const urlParts = this.$props.src.split('?');
                        return `${urlParts[0]}${this.suffix}?${urlParts[1]},${tmpTransforms}`;
                    }
                    return `${this.$props.src}${this.suffix}?${this.transforms}`;
                }
                // If src already includes transforms, then append any extras to the end
                if (this.$props.src.indexOf('?tr=') > -1) {
                    return `${this.$props.src},${tmpTransforms}`
                }
                return `${this.$props.src}?tr=${tmpTransforms}`;
            }
            if (this.$props.src && this.imageType) {
                let thumbURL = this.$props.src.split('?')[0];
                const imgPath = thumbURL.split(`/${this.imageType}/`)[1];
                // Video Type Thumbnails require that the transforms be at the end e.g: `?so=`
                if (this.isVideoSlide) {
                    thumbURL = `${process.env.VUE_APP_IMG_KIT_BASE}${
                        this.imagePath ? this.imagePath + '/' : ''
                    }${imgPath}${this.suffix}?${this.transforms}`;

                    return thumbURL;
                }

                thumbURL = `${process.env.VUE_APP_IMG_KIT_BASE}${this.imagePath ? this.imagePath + '/' : ''}${
                    this.transforms
                }/${imgPath}${this.suffix}`;
                return thumbURL;
            }

            return this.$props.src;
        },
        videoURL() {
            //Fallback for imgKit size limit errors
            //Returns src url with imgkit transoformations exlcludiing thumbnail conversion
            const videoTransforms = this.transforms + ',du-1,q-50';
            return this.thumbnailURL.replace(this.transforms, videoTransforms).replace(this.suffix, '');
        },
        isVideoSlide() {
            const isVideoSlide =
                this.type === tributeMediaTypeEnum.VIDEO ||
                this.type === tributeMediaTypeEnum.INTRO ||
                this.type === tributeMediaTypeEnum.OUTRO;
            return isVideoSlide;
        },
    },
    methods: {
        thumbnailLoadError(e) {
            console.log('image thumbnail error', e);
            if (!this.hasErrored) {
                if (this.$props.retryOnFail && this.retries < MAX_RETRIES) {
                    console.log('Retry thumbnail before falling back', `${MAX_RETRIES} - ${this.retries} retries left`);
                    setTimeout(() => {
                        this.retries++;
                    }, RETRY_TIMEOUT * (this.retries + 1) * RETRY_PAD);
                } else {
                    console.log('retries have expired, setting error state', this.thumbnailURL)
                    e.target.src = this.$props.src;
                    // Prevent infinite error loop
                    this.hasErrored = true;
                }
            }
        },
        onLoadFinish(e) {
            // console.log('loading finished', this.$props.src);
            this.$nextTick(() => {
                this.$emit('thumbnailLoaded');
            });
        }
    },
};
</script>

<style lang="scss" scoped></style>
